import { useState, useCallback } from "react";
import { REACT_APP_OMS_BASE_URL } from "../constants/constants";
import tokenUtils from "../utils/tokenUtils";

const callModificationApi = async (action, orderId) => {
  const token = tokenUtils.getToken();
  const endpoint = `${REACT_APP_OMS_BASE_URL}/${action}-order-modification-request/${orderId}`;

  const headers = {
    accept: "application/json",
    "accept-language": "en-US,en;q=0.7",
    "cache-control": "no-cache",
    "content-type": "application/json",
    "content-length": "0",
    origin: "https://adminsupport.infinitylearn.com",
    pragma: "no-cache",
    priority: "u=1, i",
    referer: "https://adminsupport.infinitylearn.com/",
    "auth-token": token || "",
  };

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers,
    });

    if (!response.ok) {
      const errorJson = await response.json().catch(() => ({}));
      const errorMsg =
        errorJson?.message ||
        errorJson?.detail ||
        `Failed to ${action} the modification request. Please try again.`;
      throw new Error(errorMsg);
    }

    const text = await response.text();
    return text ? JSON.parse(text) : { success: true };
  } catch (err) {
    throw err;
  }
};

export const useOrderModificationActions = ({ onSuccess, onError } = {}) => {
  const [loadingMap, setLoadingMap] = useState({});

  const setOrderLoading = (orderId, actionType) =>
    setLoadingMap((prev) => ({ ...prev, [orderId]: actionType }));

  const handleAction = useCallback(
    async (action, order) => {
      const { order_id } = order;
      setOrderLoading(order_id, action);

      try {
        const result = await callModificationApi(action, order_id);
        onSuccess?.(action, order, result);
      } catch (err) {
        console.error(`[useOrderModificationActions] ${action} failed:`, err);
        onError?.(action, order, err.message);
      } finally {
        setOrderLoading(order_id, null);
      }
    },
    [onSuccess, onError],
  );

  const acceptModification = useCallback(
    (order) => handleAction("accept", order),
    [handleAction],
  );

  const rejectModification = useCallback(
    (order) => handleAction("reject", order),
    [handleAction],
  );

  const isOrderLoading = (orderId) => loadingMap[orderId] || null;

  return { acceptModification, rejectModification, isOrderLoading };
};
